import { toast } from "react-toastify"
import styled from "styled-components"

import { useLazyUpdateThemeQuery } from "store/api/dashboardApi"
import { useAppDispatch, useAppSelector } from "store/hooks"
import { setUserActiveTheme } from "store/tempDataSlise"
import { ContractorThemeKey, ContractorThemes } from "constants/ContractorThemes"

const ModalContent = styled.div`
  max-height: calc(90vh - 145px);
  overflow-y: auto;
  overflow-x: hidden;
  padding: 20px 0 0 0;
`

const HeaderLine = styled.div`
  display: flex;
  align-items: center;
  padding: 0 20px;

  p {
    font-size: 18px;
    font-weight: 500;
  }
`

const ThemesListWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  padding: 20px 16px;
  width: 100%;
`

const ThemeContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 30%;

  @media (max-width: 684px) {
    min-width: 40%;
  }
`

const ThemeWrapper = styled.div<{ isActive: boolean }>`
  position: absolute;
  top: ${({ isActive }) => (isActive ? "4px" : "-3px")};
  left: ${({ isActive }) => (isActive ? "4px" : "-3px")};
  width: ${({ isActive }) => (isActive ? "calc(100% - 8px)" : "calc(100% + 6px)")};
  height: ${({ isActive }) => (isActive ? "calc(100% - 8px)" : "calc(100% + 6px)")};
  border: ${({ isActive }) => (isActive ? "3px" : "10px")} solid transparent;
  border-color: #ffffff;
  border-radius: ${({ isActive }) => (isActive ? "12px" : "19px")};
  padding: 2px;
`

const ThemeBlock = styled.div<{ backgroundStyle: string }>`
  position: relative;
  width: 100%;
  height: 120px;
  border-radius: 16px;
  background: ${({ backgroundStyle }) => backgroundStyle};
  cursor: pointer;
`

const ThemeText = styled.span`
  margin: 2px 0 0 4px;
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  color: var(--text-primary);
`

export const AccountSettingsThemes = () => {
  const dispatch = useAppDispatch()
  const userActiveTheme = useAppSelector(state => state.tempData.userActiveTheme)
  const [triggerUpdateTheme] = useLazyUpdateThemeQuery()

  const handleUpdateError = (userTheme: ContractorThemeKey) => {
    toast.error("Failed to update theme, please try again later")
    dispatch(setUserActiveTheme(userTheme))
  }

  const handleThemeChange = async (themeId: ContractorThemeKey) => {
    const userTheme = userActiveTheme
    dispatch(setUserActiveTheme(themeId))

    try {
      const res = await triggerUpdateTheme(themeId)

      if (res.error) {
        handleUpdateError(userTheme)
      }
    } catch {
      handleUpdateError(userTheme)
    }
  }

  return (
    <>
      <ModalContent>
        <HeaderLine>
          <p>Choose a theme</p>
        </HeaderLine>
        <ThemesListWrapper>
          {Object.entries(ContractorThemes).map(([theme, themeData]) => (
            <ThemeContainer key={theme}>
              <ThemeBlock
                onClick={() => handleThemeChange(theme as ContractorThemeKey)}
                backgroundStyle={themeData.backgroundGradient}
              >
                <ThemeWrapper isActive={userActiveTheme === theme} />
              </ThemeBlock>
              <ThemeText>{themeData.title}</ThemeText>
            </ThemeContainer>
          ))}
        </ThemesListWrapper>
      </ModalContent>
    </>
  )
}
